// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pacific/App/Components/Common/SectionWrapper";
import EspotCard from "@pacific/App/Widgets/PromotionalEspots/EspotCard";
import { getSystemColor, layoutContainer, xScreen } from "@pacific/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

interface EspotCardFields {
    fields: {
        title: string;
        buttonLabel: string;
        description: string;
        buttonLink: LinkFieldValue;
        imageUrl: string;
        openInNewTab: boolean;
        elementId: string;
    };
}

const enum fields {
    cards = "cards",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.cards]: EspotCardFields[];
    };
}

type Props = OwnProps;

interface EspotCardsWidgetStyles {
    container?: InjectableCss;
    cardsWrapper?: InjectableCss;
    content?: InjectableCss;
}

const espotCardsWidgetStyles: EspotCardsWidgetStyles = {
    container: {
        css: css`
            padding-bottom: 64px;
            ${({ backgroundColor }: any) => {
                if (backgroundColor) {
                    return css`
                    background: linear-gradient(
                        to bottom,
                        transparent 0,
                        transparent 12px,
                        ${backgroundColor} 12px,
                        ${backgroundColor} 100%
                    );
                    ${xScreen("lte", "sm")} {
                        background: ${backgroundColor};
                    }
                    `;
                }
            }}
            ${xScreen("lte", "sm")} {
                padding-bottom: 56px;
            }
            ${xScreen("lte", "sm")} {
                padding-top: 32px;
                padding-bottom: 32px;
            }
        `,
    },
    cardsWrapper: {
        css: css`
            ${layoutContainer}
            display: flex;
            flex-wrap: wrap;
            gap: 22px;

            ${xScreen("lte", "md")} {
                flex-direction: column;
                gap: 32px;
            }
        `,
    },
    content: {
        css: css`
            ${({ numberOfCards }: any) => {
                if (numberOfCards >= 3) {
                    return css`
                        width: calc((100% - 44px) / 3);
                    `;
                    // eslint-disable-next-line no-else-return
                } else if (numberOfCards === 2) {
                    return css`
                        width: calc((100% - 22px) / 2);
                    `;
                } else {
                    return css`
                        width: 100%;
                    `;
                }
            }}
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
};

const EspotCardsWidget: React.FC<Props> = ({
    fields: { backgroundColor, cards },
}) => {
    if (!cards || cards.length === 0) {
        return null;
    }
    const styles = espotCardsWidgetStyles;

    return (
        <StyledWrapper {...styles.container} {...({ backgroundColor } as any)} className="espotCardsWrapper">
            <StyledWrapper {...styles.cardsWrapper}>
                {cards.map((card, index) => (
                    <StyledWrapper key={index.toString()} {...styles.content} {...({ numberOfCards: cards.length } as any)}>
                        <EspotCard
                            buttonLabel={card.fields.buttonLabel}
                            title={card.fields.title}
                            description={ card.fields.description}
                            buttonLink={card.fields.buttonLink}
                            imageUrl={card.fields.imageUrl}
                            openInNewTab={card.fields.openInNewTab}
                            elementId={card.fields.elementId}
                        />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: EspotCardsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: getSystemColor("common.ui14"),
            },
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "imageUrl",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "buttonLabel",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "elementId",
                        displayName: "Element ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
